'use client'

import {
  filterMenuByEnabledModules,
  getMenuByKey,
} from '@/config/dynamicMenuConfig'
import { usePathname } from 'next/navigation'
import { createContext, useContext, useEffect, useState } from 'react'

const DynamicMenuContext = createContext(undefined)

export function DynamicMenuProvider({ children }) {
  const [spaceData, setSpaceData] = useState(null)
  const [isSpaceSelected, setIsSpaceSelected] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const pathname = usePathname()

  // Mount + load space data once (localStorage is synchronous — no retries needed)
  useEffect(() => {
    setIsMounted(true)

    try {
      const savedSpaceData = localStorage.getItem('spaceData')
      if (savedSpaceData) {
        const parsed = JSON.parse(savedSpaceData)
        const spaceName =
          parsed.name ||
          parsed.spaceName ||
          parsed.title ||
          parsed.organizationName

        if (parsed && spaceName) {
          setSpaceData({
            id: parsed._id || parsed.id || parsed.spaceId,
            _id: parsed._id || parsed.id || parsed.spaceId,
            name: spaceName,
            logo: parsed.logo,
            logoIcon: parsed.logoIcon,
            spaceLogo: parsed.spaceLogo,
            enabledModules: parsed.enabledModules || [],
            spaceCategory: parsed.spaceCategory || parsed.category,
            totalFollowers: parsed.totalFollowers || parsed.followers || 0,
            status: parsed.status || 'active',
            accountId: parsed.accountId || '',
            additionalCategories: parsed.additionalCategories || '',
            salahSettingsId: parsed.salahSettingsId || '',
            bankConnected: parsed.bankConnected || '',
            fundRaisingStripeId: parsed.fundRaisingStripeId || '',
          })
          setIsSpaceSelected(true)
        }
      }
    } catch (error) {
      console.error('DynamicMenuContext - Error loading space:', error)
    }

    setIsInitialized(true)
  }, [])

  // If on a space route without data, try loading from localStorage
  useEffect(() => {
    if (!isInitialized || isSpaceSelected) return

    const spaceRoutes = [
      '/manage-space-dashboard',
      '/manage-events',
      '/manage-coupons',
      '/manage-fundraising',
      '/manage-salah',
      '/manage-screens',
      '/manage-devices',
      '/manage-settings',
      '/manage-hardware',
    ]

    const isInSpaceRoute = spaceRoutes.some((route) =>
      pathname.startsWith(route)
    )
    if (!isInSpaceRoute) return

    try {
      const savedSpaceData = localStorage.getItem('spaceData')
      if (savedSpaceData) {
        const parsed = JSON.parse(savedSpaceData)
        const spaceName =
          parsed.name ||
          parsed.spaceName ||
          parsed.title ||
          parsed.organizationName

        if (parsed && spaceName) {
          setSpaceData({
            id: parsed._id || parsed.id || parsed.spaceId,
            _id: parsed._id || parsed.id || parsed.spaceId,
            name: spaceName,
            logo: parsed.logo,
            logoIcon: parsed.logoIcon,
            spaceLogo: parsed.spaceLogo,
            enabledModules: parsed.enabledModules || [],
            spaceCategory: parsed.spaceCategory || parsed.category,
            totalFollowers: parsed.totalFollowers || parsed.followers || 0,
            status: parsed.status || 'active',
            accountId: parsed.accountId || '',
            additionalCategories: parsed.additionalCategories || '',
            salahSettingsId: parsed.salahSettingsId || '',
            bankConnected: parsed.bankConnected || '',
            fundRaisingStripeId: parsed.fundRaisingStripeId || '',
          })
          setIsSpaceSelected(true)
        }
      }
    } catch (error) {
      console.error('DynamicMenuContext - Error loading for route:', error)
    }
  }, [pathname, isSpaceSelected, isInitialized])

  const selectSpace = (space) => {
    if (!space) return

    const spaceName =
      space.name || space.spaceName || space.title || space.organizationName

    const normalizedSpace = {
      ...space,
      id: space._id || space.id || space.spaceId,
      _id: space._id || space.id || space.spaceId,
      name: spaceName,
    }

    setSpaceData(normalizedSpace)
    setIsSpaceSelected(true)

    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('spaceData', JSON.stringify(normalizedSpace))
      } catch (error) {
        console.error('DynamicMenuContext - Error saving space:', error)
      }
    }
  }

  const clearSpace = () => {
    setSpaceData(null)
    setIsSpaceSelected(false)

    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem('spaceData')
        localStorage.removeItem('activeMenuStack')
      } catch (error) {
        console.error('DynamicMenuContext - Error clearing space:', error)
      }
    }
  }

  const getFilteredMenu = (menuKey) => {
    const menuItems = getMenuByKey(menuKey)

    if (spaceData && spaceData.enabledModules) {
      return filterMenuByEnabledModules(menuItems, spaceData.enabledModules)
    }

    return menuItems
  }

  const isModuleEnabled = (moduleName) => {
    if (!spaceData || !spaceData.enabledModules) {
      return true
    }

    if (
      moduleName?.toLowerCase() === 'settings' ||
      moduleName?.toLowerCase() === 'dashboard'
    ) {
      return true
    }

    return spaceData.enabledModules.some((module) => {
      const enabledModuleName = module?.name || module
      const enabledModuleLower = enabledModuleName?.toLowerCase()
      const moduleNameLower = moduleName?.toLowerCase()

      if (
        (enabledModuleLower === 'devices' && moduleNameLower === 'kiosk') ||
        (enabledModuleLower === 'kiosk' && moduleNameLower === 'devices')
      ) {
        return true
      }

      return enabledModuleLower === moduleNameLower
    })
  }

  const value = {
    spaceData,
    isSpaceSelected,
    isInitialized,
    isMounted,
    selectSpace,
    clearSpace,
    getFilteredMenu,
    isModuleEnabled,
  }

  return (
    <DynamicMenuContext.Provider value={value}>
      {children}
    </DynamicMenuContext.Provider>
  )
}

export function useDynamicMenuContext() {
  const context = useContext(DynamicMenuContext)

  if (context === undefined) {
    throw new Error(
      'useDynamicMenuContext must be used within a DynamicMenuProvider'
    )
  }

  return context
}

export default DynamicMenuContext
